// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable, throwError } from 'rxjs';
import { catchError, filter, flatMap, mergeMap, take, timeout } from 'rxjs/operators';
import { API_KEY } from './environment.configuration';
import { SPAAuthService } from './services/spa-auth.service';
import { CoreState, getTokenState, Token, TriggerLoginRefreshAction } from './state';

const HTTP_UNAUTHORIZED = 401;
const TOKEN_REFRESH_TIMEOUT = 5 * 1000; // 5 seconds

@Injectable()
export class AuthHttpInterceptor implements HttpInterceptor {
  readonly token$: Observable<Token | undefined>;

  constructor(private store: Store<CoreState>, private auth: SPAAuthService, @Inject(API_KEY) private api: string) {
    this.token$ = store.pipe(select(getTokenState));
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.auth.getIdToken$().pipe(
      mergeMap((token: any) => {
        const tokenReq = req.clone({
          setHeaders: { Authorization: `Bearer ${token}` }
        });
        return next.handle(tokenReq);
      }),
      catchError(err => throwError(err))
    );
  }

  private isApiRequest(request: HttpRequest<any>) {
    return request.url.startsWith(this.api);
  }

  private requestWithLatestToken(request: HttpRequest<any>, next: HttpHandler) {
    return this.token$.pipe(
      take(1),
      flatMap(token => this.requestWithAuth(request, next, token))
    );
  }

  private isUnauthorizedHttpError(error: any): error is HttpErrorResponse {
    return error instanceof HttpErrorResponse && error.status === HTTP_UNAUTHORIZED;
  }

  private requestWithNewToken(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.store.dispatch(new TriggerLoginRefreshAction());
    return this.token$.pipe(
      filter(token => this.isTokenValid(token)),
      take(1),
      timeout(TOKEN_REFRESH_TIMEOUT),
      flatMap(token => this.requestWithAuth(request, next, token))
    );
  }

  private requestWithAuth(request: HttpRequest<any>, next: HttpHandler, token: Token | undefined) {
    if (!this.isTokenValid(token)) {
      return next.handle(request);
    }
    const req = request.clone({
      setHeaders: {
        Authorization: `Bearer ${token.idToken}`
      }
    });
    return next.handle(req);
  }
  private isTokenValid(token: Token | undefined): token is Token {
    return token !== undefined && token.expiresAt >= new Date();
  }
}



// WEBPACK FOOTER //
// ./src/app/core/auth-http-interceptor.ts